import type { ScoreTimeline, PracticeHand, Chord } from '@/types';
import { getNoteHand, getResolvedStaff } from './hand';

/** Chords whose sounding window contains `currentTime` (shared by
 * `computeExpectedPitches` and `computeExpectedHandMap` so both stay in
 * sync on the exact same windowing rule). */
function activeChordsInWindow(timeline: ScoreTimeline, currentTime: number): Chord[] {
  const active: Chord[] = [];
  for (const chord of timeline.chords) {
    const chordDuration = Math.max(...chord.notes.map((n) => n.duration));
    if (chord.startTime <= currentTime && currentTime < chord.startTime + chordDuration + 0.05) {
      active.push(chord);
    }
    if (chord.startTime > currentTime + 0.1) break;
  }
  return active;
}

/**
 * OTO-21: pure computation of the expected MIDI pitch set for keyboard
 * highlighting, given the current playback time and hand filter. Extracted
 * from `PracticeView` (previously recomputed inline on every 60fps render)
 * so a transient store subscription (`useExpectedNotes`) can call it and
 * only trigger a React re-render when the resulting set actually changes.
 */
export function computeExpectedPitches(
  timeline: ScoreTimeline | null | undefined,
  currentTime: number,
  hand: PracticeHand
): Set<number> {
  const pitches = new Set<number>();
  if (!timeline) return pitches;

  for (const chord of activeChordsInWindow(timeline, currentTime)) {
    for (const note of chord.notes) {
      if (hand === 'both' || getNoteHand(note) === hand) {
        pitches.add(note.pitch);
      }
    }
  }

  return pitches;
}

/**
 * OTO-23: expected-pitch -> staff map, for PianoKeyboard to color key glow
 * by hand instead of by pitch (PianoKeyboard receives only pitch sets, not
 * Note objects -- this is the data plumbing that makes staff available
 * there). Same windowing/hand-filter rules as `computeExpectedPitches`.
 *
 * Unison rule: if a pitch is expected on BOTH staves at once, staff 1
 * (right hand) wins -- a physical key can only glow one color, and this
 * tie-break is deterministic regardless of note processing order.
 */
export function computeExpectedHandMap(
  timeline: ScoreTimeline | null | undefined,
  currentTime: number,
  hand: PracticeHand
): Map<number, 1 | 2> {
  const staffByPitch = new Map<number, 1 | 2>();
  if (!timeline) return staffByPitch;

  for (const chord of activeChordsInWindow(timeline, currentTime)) {
    for (const note of chord.notes) {
      if (hand === 'both' || getNoteHand(note) === hand) {
        if (staffByPitch.get(note.pitch) !== 1) {
          staffByPitch.set(note.pitch, getResolvedStaff(note));
        }
      }
    }
  }

  return staffByPitch;
}
